/* eslint-disable @typescript-eslint/no-explicit-any */
import { PropsWithChildren, useCallback } from 'react';
import Select from 'react-select';
import { FilterOptionOption } from 'react-select/dist/declarations/src/filters';
import { useFormContext, Controller } from 'react-hook-form';
import { getErrorText } from 'utils/getErrorText';
import { makeSelectOption, makeSelectOptions } from 'utils/makeSelectOptions';
import { ErrorIcon } from 'components/Core/ErorrIcon/ErrorIcon';
import { ReactComponent as StarIcon } from 'assets/star_required_icon.svg';
import { Obj } from 'types/common';
import { MenuList, Option, getDropdownIndicator, getCustomStyles, MultiValue, SingleValue, MultiValueRemove } from '.';
import { ISelectComponentProps } from './types';
import styles from './select.module.scss';

function SelectComponent<T extends Obj>({
  name,
  options,
  onInputChange,
  onSelect,
  onMultiSelect,
  afterSelect,
  customOption,
  customSingleValue,
  customMultiValue,
  customIcon,
  validation,
  isClearable,
  label,
  styleProps,
  maxMenuHeight,
  optionLabel,
  optionValue,
  showRequiredIcon = true,
  isMulti = false,
  required = false,
  isSearchSelect = false,
  isLoading = false,
  disableFiltering = false,
  menuPlacement = 'auto',
  pattern,
  errorText,
  showErrorIcon = false,
  placeholder,
  ...props
}: PropsWithChildren<ISelectComponentProps<T>>) {
  const formContext = useFormContext();

  const { control } = formContext;

  /**
   * function to get the value for the select component, for string values cases.
   * transforms the value to a select option format if it is not already in that format.
   */
  const getSelectValue = useCallback(
    (value: any | any[]) => {
      if (!value) return undefined;
      const val = isMulti ? value[0] : value;
      if (!val || typeof val === 'object') return value;
      return (isMulti ? makeSelectOptions : makeSelectOption)(
        value,
        optionValue && optionLabel
          ? {
              value: optionValue as string,
              label: optionLabel as string,
            }
          : undefined
      );
    },
    [isMulti, optionValue, optionLabel]
  );

  const onInputChangeHandler = useCallback(
    (val: string) => {
      if (onInputChange) {
        onInputChange(val);
      }
    },
    [onInputChange]
  );

  const filterOptions = useCallback(
    (candidate: FilterOptionOption<T>, input: string) => {
      return disableFiltering ? true : candidate.label.toLowerCase().includes(input.toLowerCase());
    },
    [disableFiltering]
  );

  return (
    <>
      {label && (
        <div className={styles.labelWrapper}>
          <p className={styles.subtitle}>{label}</p>
          {required && showRequiredIcon && <StarIcon className={styles.labelRequiredIcon} />}
        </div>
      )}
      <div className={styles.select} style={styleProps?.selectStyle}>
        {!label && required && showRequiredIcon && <StarIcon className={styles.requiredIcon} />}
        <Controller
          control={control}
          name={name}
          rules={{ required: required, validate: validation?.validate }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <>
              <Select
                id={name}
                instanceId={name}
                styles={getCustomStyles<T>({
                  isError: !!error,
                  controlStyles: styleProps,
                })}
                options={options as T[]}
                placeholder={placeholder || ''} // to prevent Select to use ts own string with no placeholder provided in
                getOptionLabel={optionLabel ? (val) => val[optionLabel] : undefined}
                getOptionValue={optionValue ? (val) => val[optionValue] : undefined}
                filterOption={filterOptions}
                value={getSelectValue(value) as any}
                onInputChange={onInputChangeHandler}
                onChange={(val) => {
                  if (isMulti && onMultiSelect) onMultiSelect((val || []) as T[]);
                  else if (onSelect) onSelect(val as T);
                  else onChange(val);
                  if (afterSelect) afterSelect();
                }}
                isClearable={isClearable === undefined ? isMulti || !required : isClearable}
                components={{
                  MenuList: (props) => MenuList(props, optionValue, isLoading, maxMenuHeight),
                  Option: (props) => Option(props, optionValue, customOption),
                  DropdownIndicator: (props) => getDropdownIndicator(props, isSearchSelect, customIcon),
                  MultiValueRemove,
                  MultiValueLabel: (props) => MultiValue(props, customMultiValue),
                  SingleValue: (props) => SingleValue(props, customSingleValue),
                }}
                isMulti={isMulti}
                menuPlacement={menuPlacement}
                {...props}
                // menuIsOpen
              />
              {error && showErrorIcon && (
                <ErrorIcon
                  name={name}
                  message={getErrorText((error?.type as string) || '', {
                    errorText,
                    patternMsg: pattern?.message,
                    validateErrorText: validation?.message,
                  })}
                  stylesProp={{ top: '23%' }}
                />
              )}
            </>
          )}
        />
      </div>
    </>
  );
}

export default SelectComponent;
